import { z } from 'zod';

/**
 * Phone Number Utilities
 * US format only: (xxx)xxx-xxxx
 */

/**
 * Normalizes phone number to digits only
 * Accepts: (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890, etc.
 * Returns: 1234567890 (10 digits)
 */
export function normalizePhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10 ? digits : null;
}

/**
 * Formats phone number for display
 * Input: 1234567890 (or any format)
 * Returns: (123) 456-7890
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10) return phone; // Return as-is if not 10 digits
  
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Validates if phone number is valid US format
 * Returns: { valid: boolean, normalized: string | null, error?: string }
 */
export function validatePhone(phone: string | null | undefined): {
  valid: boolean;
  normalized: string | null;
  error?: string;
} {
  if (!phone || phone.trim() === '') {
    return { valid: true, normalized: null }; // Empty is valid (optional field)
  }

  const digits = phone.replace(/\D/g, '');
  
  if (digits.length !== 10) {
    return {
      valid: false,
      normalized: null,
      error: 'Phone number must be 10 digits'
    };
  }

  return {
    valid: true,
    normalized: digits
  };
}

/**
 * Email Utilities
 */

/**
 * Normalizes email to lowercase
 */
export function normalizeEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  return email.toLowerCase().trim();
}

/**
 * Validates email format
 * Returns: { valid: boolean, normalized: string | null, error?: string }
 */
export function validateEmail(email: string | null | undefined): {
  valid: boolean;
  normalized: string | null;
  error?: string;
} {
  if (!email || email.trim() === '') {
    return { valid: true, normalized: null }; // Empty is valid (optional field)
  }

  const normalized = email.toLowerCase().trim();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(normalized)) {
    return {
      valid: false,
      normalized: null,
      error: 'Please enter a valid email address (e.g., name@example.com)'
    };
  }

  return {
    valid: true,
    normalized
  };
}

/**
 * Zod Schemas for Form Validation
 */

/**
 * Email schema - optional field, validates format when provided
 */
export const emailSchema = z
  .string()
  .email('Please enter a valid email address (e.g., name@example.com)')
  .optional()
  .or(z.literal(''));

/**
 * Email schema - required field
 */
export const requiredEmailSchema = z
  .string()
  .min(1, 'Email address is required')
  .email('Please enter a valid email address (e.g., name@example.com)');

/**
 * Phone schema - optional field, validates US format when provided
 * Accepts: (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890
 */
export const phoneSchema = z
  .string()
  .optional()
  .or(z.literal(''))
  .refine(
    (val) => {
      if (!val || val.trim() === '') return true; // Empty is valid
      const digits = val.replace(/\D/g, '');
      return digits.length === 10;
    },
    {
      message: 'Please enter a valid US phone number (e.g., (123) 456-7890)'
    }
  );

/**
 * Phone schema - required field
 */
export const requiredPhoneSchema = z
  .string()
  .min(1, 'Phone number is required')
  .refine(
    (val) => {
      const digits = val.replace(/\D/g, '');
      return digits.length === 10;
    },
    {
      message: 'Please enter a valid US phone number (e.g., (123) 456-7890)'
    }
  );

/**
 * Helper function to format phone as user types (for input fields)
 * Returns formatted string: (123) 456-7890
 */
export function formatPhoneInput(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limitedDigits = digits.slice(0, 10);
  
  // Format as (xxx) xxx-xxxx
  if (limitedDigits.length === 0) return '';
  if (limitedDigits.length <= 3) return `(${limitedDigits}`;
  if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  }
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
}

/**
 * Standardized error messages
 */
export const ValidationMessages = {
  email: {
    invalid: 'Please enter a valid email address (e.g., name@example.com)',
    required: 'Email address is required',
    alreadyExists: 'This email is already registered'
  },
  phone: {
    invalid: 'Please enter a valid US phone number (e.g., (123) 456-7890)',
    required: 'Phone number is required',
    tooShort: 'Phone number must be 10 digits',
    alreadyExists: 'This phone number is already in use'
  }
} as const;

